import { CommonActions } from '@react-navigation/native';
import { Stack, useFocusEffect, useLocalSearchParams, useNavigation } from 'expo-router';
import Head from 'expo-router/head';
import { useCallback } from 'react';

import { VisuallyHiddenH1 } from '@fhs/ui';
import { StoreLocator } from '@fhs-legacy/frontend/src/pages/store-locator';
import { useAreFlagsReady, useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useForwardToStoreLocator } from '../../utils/use-forward-to-store-locator';

export { ErrorBoundary } from '@fhs/routing/src/components/page-dev-error-boundary';

/**
 * The v2 locator's URL. With v2 off, the legacy locator keeps living on `service-mode`, so this
 * page forwards there. It waits for LaunchDarkly first: the flag reads as off until then, and
 * forwarding on that would bounce v2 guests through `service-mode` and back.
 */
function StoreLocatorPage() {
  const navigation = useNavigation();
  const params = useLocalSearchParams();
  const enableStoreLocatorV2 = useFlag('enable-store-locator-v2');
  const areFlagsReady = useAreFlagsReady();
  const forwardToLegacy = areFlagsReady && !enableStoreLocatorV2;

  useForwardToStoreLocator(params, forwardToLegacy, 'service-mode');

  // `service-mode` is this stack's initial route, so a direct visit here builds it underneath as
  // a second locator. Both are the same page, so back would only land on the locator again.
  useFocusEffect(
    useCallback(() => {
      if (!areFlagsReady || !enableStoreLocatorV2) {
        return;
      }

      const { routes } = navigation.getState() ?? {};
      if (!routes?.some(route => route.name === 'service-mode')) {
        return;
      }

      navigation.dispatch(state => {
        const remaining = state.routes.filter(route => route.name !== 'service-mode');
        return CommonActions.reset({ ...state, routes: remaining, index: remaining.length - 1 });
      });
    }, [areFlagsReady, enableStoreLocatorV2, navigation])
  );

  if (forwardToLegacy) {
    // Nothing to show while forwarding; `service-mode` mounts the legacy locator itself.
    return <Stack.Screen options={{ headerShown: false, animation: 'none' }} />;
  }

  return (
    <>
      <Head>
        <title>Find a Firehouse - Firehouse Subs</title>
      </Head>
      <VisuallyHiddenH1 title="Store Locator" />
      <StoreLocator />
    </>
  );
}

export default StoreLocatorPage;
